import { ChangeEvent, FormEvent, useState } from "react";
import CustomLabel from "../../common/CustomLabel";
import useFetchWithAuth from "../../hooks/useFetchWithAuth";
import { useToast } from "../../../context/ToastContext";
import devLogger from "../../../utils/devLogger";
import { getApiUrl } from "../../../utils/api";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import LoadingAnimation from "../../common/LoadingAnimation";

const ProfilePasswordChange = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchWithAuth } = useFetchWithAuth();
  const { toast } = useToast();

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    passwordConfirm: "",
  });

  const [errors, setErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
    passwordConfirm?: string;
  }>({
    oldPassword: undefined,
    newPassword: undefined,
    passwordConfirm: undefined,
  });

  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {
      oldPassword:
        !passwordData.oldPassword || passwordData.oldPassword.length < 8
          ? "Password should be at least 8 characters"
          : undefined,
      newPassword:
        !passwordData.newPassword || passwordData.newPassword.length < 8
          ? "Password should be at least 8 characters"
          : undefined,
      passwordConfirm:
        passwordData.newPassword !== passwordData.passwordConfirm
          ? "Passwords do not match"
          : undefined,
    };

    setErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some((error) => error !== undefined);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "oldPassword") {
      setErrors((prev) => ({
        ...prev,
        oldPassword:
          !value || value.length < 8
            ? "Password should be at least 8 characters"
            : undefined,
      }));
    } else if (name === "newPassword") {
      setErrors((prev) => ({
        ...prev,
        newPassword:
          !value || value.length < 8
            ? "Password should be at least 8 characters"
            : undefined,
        // Also update password confirmation validation
        passwordConfirm:
          passwordData.passwordConfirm && passwordData.passwordConfirm !== value
            ? "Passwords do not match"
            : undefined,
      }));
    } else if (name === "passwordConfirm") {
      setErrors((prev) => ({
        ...prev,
        passwordConfirm:
          value !== passwordData.newPassword
            ? "Passwords do not match"
            : undefined,
      }));
    }

    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { oldPassword, newPassword } = passwordData;

      const response = await fetchWithAuth(
        getApiUrl("/auth/password/change-password"),
        {
          method: "POST",
          body: JSON.stringify({ oldPassword, newPassword }),
        }
      );
      devLogger.log("🚀 ~ handleSubmit ~ response:", response);

      toast({
        type: "success",
        title: "Password changed successfully!",
        subtitle: "You will be redirected to your profile shortly",
      });

      // Reset form
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        passwordConfirm: "",
      });
      setTimeout(() => {
        navigate("/profile");
      }, 3000);
    } catch (error) {
      let errorMessage = "Failed to change password";

      if (error instanceof Error) {
        errorMessage = error.message;

        // Handle specific error cases from API
        if (errorMessage.includes("401") || errorMessage.includes("403")) {
          errorMessage = "Current password is incorrect";
        }
      }

      devLogger.error("🚀 ~ handleSubmit ~ errorMessage:", errorMessage);
      toast({
        type: "error",
        title: "Error",
        subtitle: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-[calc(100%-32px)] mx-auto pt-6"
    >
      <CustomLabel
        labelText="Old Password"
        labelTextClass="px-0 mb-2 text-sm font-bold"
        labelWrapClass="!gap-1 mb-4"
      >
        <input
          type="password"
          name="oldPassword"
          value={passwordData.oldPassword}
          onChange={handleInputChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-7 leading-tight focus:outline-none focus:shadow-outline"
        />

        {errors.oldPassword && (
          <p className="text-danger font-normal text-[10px] leading-3 mt-1 px-4">
            {errors.oldPassword}
          </p>
        )}
      </CustomLabel>

      <CustomLabel
        labelText="New Password"
        labelTextClass="px-0 mb-2 text-sm font-bold"
        labelWrapClass="!gap-1 mb-4"
      >
        <div className="relative w-full">
          <input
            type={showPassword ? "text" : "password"}
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handleInputChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-7 leading-tight focus:outline-none focus:shadow-outline"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-4 flex items-center text-gray-7"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? (
              <span className="material-icons">
                <FaEyeSlash size={20} className="text-dark-7" />
              </span>
            ) : (
              <span className="material-icons">
                <FaEye size={20} className="text-dark-7" />
              </span>
            )}
          </button>
        </div>

        {errors.newPassword && (
          <p className="text-danger font-normal text-[10px] leading-3 mt-1 px-4">
            {errors.newPassword}
          </p>
        )}
      </CustomLabel>

      <CustomLabel
        labelText="Confirm Password"
        labelTextClass="px-0 mb-2 text-sm font-bold"
        labelWrapClass="!gap-1 mb-4"
      >
        <div className="relative w-full">
          <input
            type={showPassword ? "text" : "password"}
            name="passwordConfirm"
            value={passwordData.passwordConfirm}
            onChange={handleInputChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-7 leading-tight focus:outline-none focus:shadow-outline"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-4 flex items-center text-gray-7"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? (
              <span className="material-icons">
                <FaEyeSlash size={20} className="text-dark-7" />
              </span>
            ) : (
              <span className="material-icons">
                <FaEye size={20} className="text-dark-7" />
              </span>
            )}
          </button>
        </div>

        {errors.passwordConfirm && (
          <p className="text-danger font-normal text-[10px] leading-3 mt-1 px-4">
            {errors.passwordConfirm}
          </p>
        )}
      </CustomLabel>

      <div className="flex items-center justify-between">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full ${
            isSubmitting ? "bg-gray-400" : "bg-black hover:bg-gray-7"
          } text-white font-bold py-2 px-4 rounded-[10px] focus:outline-none focus:shadow-outline`}
        >
          {isSubmitting ? <LoadingAnimation loadingText="Saving" /> : "Save"}
        </button>
      </div>
    </form>
  );
};

export default ProfilePasswordChange;
